import React, { Component } from "react";
import { FormattedMessage } from "react-intl";
import { AxiosResponse } from "axios";

import ComposerCard from "../../../../components/ComposerCard";
import GenreCard from "../../../../components/GenreCard";
import {
    opusLanguages,
    getSelectValue,
    generalGenres,
    folksongClasses,
    planTypes,
    exerciseTypes,
    folksongTempos,
    levels,
} from "../../../../../common/parameters";

import { InputText, Checkbox, Select, Label, InputSelectCard, Radio, FormElement } from "../../../../components/Form";

import ComposerForm from "../../../../components/ComposerForm";
import { Tab, Card, StyledForm } from "../styles";

import {
    Column,
    DeleteLabel,
    StyledInput,
} from "./styles";
import { IComposer, IProps, IState, IComposersResponse, ICreateComposer } from "./interfaces";
import messages from "./messages";
import { ICheckboxEvent, IRadioEvent, ISelectEvent } from "../../../../types";
import { formatComposerName } from "../../../../../common/helpers/formatComposerName";

class Opus extends Component<IProps, IState> {
    state: IState = { composers: [], genres: [], labelInput: "", createNewComposer: false };
    componentDidMount() {
        const {
            get,
            intl: { formatMessage, locale },
        } = this.props;
        get("composer-list", (res: AxiosResponse<IComposersResponse>) => {
            const rawData = res.data.composers;
            const composers: IComposer[] = [];
            rawData.forEach(data => {
                composers.push({
                    label: formatComposerName(data.name, locale),
                    id: data.id,
                    img: data.img,
                    short: data.short,
                    card: (
                        <ComposerCard
                            img={data.img}
                            name={formatComposerName(data.name, locale)}
                            birth={data.birth}
                            death={data.death}
                            period={data.era}
                            country={data.country}
                        />
                    ),
                });
            });
            this.setState({ composers });
        });

        const genres = generalGenres(formatMessage).map(genre => {
            return {
                label: genre.label,
                id: genre.value,
                card: <GenreCard img={genre.img} name={genre.label} details={genre.details} />,
            };
        });
        this.setState({ genres });
    }
    createComposer = (data: Omit<ICreateComposer, "id">) => {
        const { post } = this.props;
        post("create-new-composer", data, res => {
            const { data } = res;
            if (data.success) {
                this.composerCreated(data.composer);
            }
        });
    };
    composerCreated = (composer: ICreateComposer) => {
        const { composers } = this.state;
        composers.push({
            label: composer.name,
            id: composer.id,
            card: (
                <ComposerCard
                    img={composer.img}
                    name={composer.name}
                    birth={composer.birth}
                    death={composer.death}
                    period={composer.era}
                    country={composer.country}
                />
            ),
        });
        this.changeOpusComposer(composer.id);
        this.setState({ composers, createNewComposer: false }, () => {
            this.saveOpusComposer();
        });
    };
    changeOpusComposer = (value: string) => {
        const composerId = value;
        const { setPlanMeta, plan, intl: { locale } } = this.props;
        if (composerId) {
            if (composerId !== "unknown") {
                const composer = this.state.composers.find(composer => composer.id === composerId)!;
                plan.meta.opus.composerName = formatComposerName(composer.short || "", locale);
                plan.meta.opus.composerImg = composer.img;
            }
        }
        setPlanMeta({ cat: "opus", prop: "composer", val: composerId });
    };
    saveOpusComposer = () => {
        const { post, plan } = this.props;
        const { meta, _id } = plan;
        const { composer } = meta.opus;
        post("save-meta-opus-composer", { composer, planId: _id });
    };
    changeOpusFolksongClass = (e: ISelectEvent) => {
        const folksongClass = e.target.value;
        const { plan, setPlanMeta, post } = this.props;
        setPlanMeta({ cat: "opus", prop: "folksongClass", val: folksongClass });
        post("save-meta-opus-folksong-class", { folksongClass, planId: plan._id });
    };
    changeOpusGenre = (value: string) => {
        const genre = value;
        const { setPlanMeta } = this.props;
        setPlanMeta({ cat: "opus", prop: "genre", val: genre });
    };
    saveOpusGenre = () => {
        const { post, plan } = this.props;
        const { meta, _id } = plan;
        const { genre } = meta.opus;
        post("save-meta-opus-genre", { genre, planId: _id });
    };
    changeOpusTitle = (e: React.FormEvent<HTMLInputElement>) => {
        const title = e.currentTarget.value;
        const { setPlanMeta } = this.props;
        setPlanMeta({ cat: "opus", prop: "title", val: title });
    };
    saveOpusTitle = () => {
        const { post, plan } = this.props;
        const { meta, _id } = plan;
        const { title } = meta.opus;
        post("save-meta-opus-title", { title, planId: _id });
    };

    changeOpusHasLyrics = (e: ICheckboxEvent) => {
        const hasLyrics = e.target.checked;
        const { plan, setPlanMeta, post } = this.props;
        setPlanMeta({ cat: "opus", prop: "hasLyrics", val: hasLyrics });
        if (!hasLyrics) {
            setPlanMeta({ cat: "opus", prop: "lyricLanguage", val: "" });
        }
        post("save-meta-opus-has-lyrics", { hasLyrics, planId: plan._id });
    };

    changeOpusLanguage = (e: ISelectEvent) => {
        const lyricLanguage = e.target.value;
        const { plan, setPlanMeta, post } = this.props;
        setPlanMeta({ cat: "opus", prop: "lyricLanguage", val: lyricLanguage });
        post("save-meta-opus-language", { lyricLanguage, planId: plan._id });
    };
    changeOpusCanon = (e: ICheckboxEvent) => {
        const canon = e.target.checked;
        const { plan, setPlanMeta, post } = this.props;
        setPlanMeta({ cat: "opus", prop: "canon", val: canon });
        post("save-meta-opus-has-canon", { canon, planId: plan._id });
    };
    changeFolksongTempo = (e: IRadioEvent) => {
        const folksongTempo = e.target.value;
        const { plan, setPlanMeta, post } = this.props;
        setPlanMeta({ cat: "opus", prop: "folksongTempo", val: folksongTempo });
        post("save-meta-opus-folksong-tempo", { folksongTempo, planId: plan._id });
    };

    changeOpusType = (e: IRadioEvent) => {
        const planType = e.target.value;
        const { plan, setPlanMeta, post } = this.props;
        if (plan.meta.opus.planType === planType) return;
        setPlanMeta({ cat: "opus", prop: "composer", val: "" });
        setPlanMeta({ cat: "opus", prop: "genre", val: "" });
        setPlanMeta({ cat: "opus", prop: "folksongClass", val: "" });
        setPlanMeta({ cat: "opus", prop: "folksongTempo", val: "" });
        setPlanMeta({ cat: "opus", prop: "exerciseType", val: "" });
        if (planType === "folk") {
            setPlanMeta({ cat: "opus", prop: "hasLyrics", val: true });
        }
        if (planType === "exercise") {
            setPlanMeta({ cat: "opus", prop: "title", val: "" });
        }
        setPlanMeta({ cat: "opus", prop: "planType", val: planType });
        post("save-meta-opus-plan-type", { planType, planId: plan._id });
    };

    changeOpusIsChildrenSong = (e: ICheckboxEvent) => {
        const isChildrenSong = e.target.checked;
        const { plan, setPlanMeta, post } = this.props;
        setPlanMeta({ cat: "opus", prop: "isChildrenSong", val: isChildrenSong });
        post("save-meta-opus-is-children-song", { isChildrenSong, planId: plan._id });
    };

    changeOpusIsChurchMusic = (e: ICheckboxEvent) => {
        const isChurchMusic = e.target.checked;
        const { plan, setPlanMeta, post } = this.props;
        setPlanMeta({ cat: "opus", prop: "isChurchMusic", val: isChurchMusic });
        post("save-meta-opus-is-church-music", { isChurchMusic, planId: plan._id });
    };

    changeOpusExerciseType = (e: IRadioEvent) => {
        const exerciseType = e.target.value;
        const { plan, setPlanMeta, post } = this.props;
        setPlanMeta({ cat: "opus", prop: "exerciseType", val: exerciseType });
        post("save-meta-opus-exercise-type", { exerciseType, planId: plan._id });
    };

    changeTeachingLevel = (e: ISelectEvent<number>) => {
        const level = e.target.value;
        const { plan, setPlanMeta, post } = this.props;
        setPlanMeta({ cat: "teaching", prop: "level", val: level });
        post("save-meta-teaching-level", { level, planId: plan._id });
    };

    changeLabelInput = (e: React.FormEvent<HTMLInputElement>) => {
        const labelInput = e.currentTarget.value;
        this.setState({ labelInput });
    };
    addLabel = (e: { preventDefault: () => void }) => {
        e.preventDefault();
        const { labelInput } = this.state;
        const { plan, setPlanMeta } = this.props;
        let labels = plan.meta.teaching.labels || [];
        if (labels.indexOf(labelInput) === -1 && labelInput !== "") {
            labels.push(labelInput);
            setPlanMeta({ cat: "teaching", prop: "labels", val: labels });
            this.setState({ labelInput: "" });
            this.saveLabel();
        }
    };
    removeLabel = (index: number) => {
        const { plan, setPlanMeta } = this.props;
        let labels = plan.meta.teaching.labels!;
        labels.splice(index, 1);
        setPlanMeta({ cat: "teaching", prop: "labels", val: labels });
        this.saveLabel();
    };
    saveLabel = () => {
        const { plan, post } = this.props;
        const { labels } = plan.meta.teaching;
        post("save-meta-teaching-labels", { labels, planId: plan._id });
    };

    render() {
        const {
            plan,
            intl: { formatMessage },
            publicView,
        } = this.props;
        const {
            meta,
            meta: { teaching },
        } = plan;
        const { composers, createNewComposer, genres, labelInput } = this.state;
        return (
            <Tab>
                {publicView && (
                    <>
                        <Column>
                            {meta.opus.planType && (
                                <FormElement>
                                    <Label>
                                        <FormattedMessage {...messages.type} />
                                    </Label>
                                    <div>
                                        {getSelectValue(planTypes(formatMessage), meta.opus.planType)}
                                        {meta.opus.isChurchMusic && `, ${formatMessage(messages.isChurchMusic)}`}
                                        {meta.opus.isChildrenSong && `, ${formatMessage(messages.isChildrenSong)}`}
                                    </div>
                                </FormElement>
                            )}
                            {meta.opus.hasLyrics && (
                                <FormElement>
                                    <FormattedMessage {...messages.isLyrics} />
                                </FormElement>
                            )}
                            {meta.opus.lyricLanguage && (
                                <FormElement>
                                    <Label>
                                        <FormattedMessage {...messages.language} />
                                    </Label>
                                    <div>{getSelectValue(opusLanguages(formatMessage), meta.opus.lyricLanguage)}</div>
                                </FormElement>
                            )}
                            {meta.opus.canon && (
                                <FormElement>
                                    <FormattedMessage {...messages.isCanon} />
                                </FormElement>
                            )}
                            {meta.teaching.level && (
                                <FormElement>
                                    <Label>
                                        <FormattedMessage {...messages.level} />
                                    </Label>
                                    <div>{getSelectValue(levels(formatMessage), meta.teaching.level)}</div>
                                </FormElement>
                            )}
                            {meta.teaching.labels && meta.teaching.labels.length > 0 && (
                                <FormElement>
                                    <Label>
                                        <FormattedMessage {...messages.labels} />
                                    </Label>
                                    <div>
                                        {meta.teaching.labels.map((label, index) => {
                                            return <Card key={label}>{label}</Card>;
                                        })}
                                    </div>
                                </FormElement>
                            )}
                        </Column>
                        <Column>
                            {meta.opus.folksongClass && (
                                <FormElement>
                                    <Label>
                                        <FormattedMessage {...messages.folksongClass} />
                                    </Label>
                                    <div>{getSelectValue(folksongClasses(formatMessage), meta.opus.folksongClass)}</div>
                                </FormElement>
                            )}
                            {meta.opus.composer && (
                                <InputSelectCard
                                    name="composer"
                                    label={formatMessage(messages.composer)}
                                    value={meta.opus.composer || ""}
                                    onChange={this.changeOpusComposer}
                                    options={composers}
                                    save={this.saveOpusComposer}
                                    disabled
                                    unknown
                                    createNew={{
                                        label: <FormattedMessage {...messages.addNewComposer} />,
                                        onClick: () => {
                                            this.setState({ createNewComposer: true });
                                        },
                                    }}
                                />
                            )}
                            {meta.opus.exerciseType && (
                                <FormElement>
                                    <Label>
                                        <FormattedMessage {...messages.exerciseType} />
                                    </Label>
                                    <div>{getSelectValue(exerciseTypes(formatMessage), meta.opus.exerciseType)}</div>
                                </FormElement>
                            )}
                            {meta.opus.title && (
                                <FormElement>
                                    <Label>
                                        <FormattedMessage {...messages.title} />
                                    </Label>
                                    <div>{meta.opus.title}</div>
                                </FormElement>
                            )}
                            {meta.opus.folksongTempo && (
                                <FormElement>
                                    <Label>
                                        <FormattedMessage {...messages.folksongTempo} />
                                    </Label>
                                    <div>{getSelectValue(folksongTempos(formatMessage), meta.opus.folksongTempo)}</div>
                                </FormElement>
                            )}
                            {meta.opus.genre && (
                                <InputSelectCard
                                    name="genre"
                                    label={formatMessage(messages.genre)}
                                    value={meta.opus.genre || ""}
                                    onChange={this.changeOpusGenre}
                                    options={genres}
                                    save={this.saveOpusGenre}
                                    disabled
                                />
                            )}
                        </Column>
                    </>
                )}
                {!publicView && (
                    <>
                        <Column>
                            <Radio
                                label={formatMessage(messages.type)}
                                value={meta.opus.planType}
                                options={planTypes(formatMessage)}
                                onChange={this.changeOpusType}
                                required
                            />
                            <Checkbox
                                label={formatMessage(messages.isChildrenSong)}
                                checked={meta.opus.isChildrenSong || false}
                                onChange={this.changeOpusIsChildrenSong}
                                disabled={meta.opus.isChurchMusic}
                            />
                            <Checkbox
                                label={formatMessage(messages.isChurchMusic)}
                                checked={meta.opus.isChurchMusic || false}
                                onChange={this.changeOpusIsChurchMusic}
                                disabled={meta.opus.isChildrenSong}
                            />
                            <Checkbox
                                label={formatMessage(messages.isLyrics)}
                                checked={meta.opus.hasLyrics || false}
                                onChange={this.changeOpusHasLyrics}
                                disabled={meta.opus.planType === "folk"}
                            />
                            {meta.opus.hasLyrics && (
                                <Select
                                    name="language"
                                    label={formatMessage(messages.language)}
                                    value={meta.opus.lyricLanguage}
                                    onChange={this.changeOpusLanguage}
                                    options={opusLanguages(formatMessage)}
                                    required={meta.opus.planType === "folk"}
                                />
                            )}
                            <Checkbox
                                label={formatMessage(messages.isCanon)}
                                checked={meta.opus.canon || false}
                                onChange={this.changeOpusCanon}
                            />
                            <Select
                                name="level"
                                label={formatMessage(messages.level)}
                                value={teaching.level}
                                onChange={this.changeTeachingLevel}
                                options={levels(formatMessage)}
                                tooltip={formatMessage(messages.levelTooltip)}
                                required
                            />
                            <FormElement>
                                <Label>
                                    <FormattedMessage {...messages.labels} />
                                </Label>
                                <div>
                                    {teaching.labels &&
                                        teaching.labels.map((label, index) => {
                                            return (
                                                <Card key={label}>
                                                    {label}
                                                    <DeleteLabel onClick={() => this.removeLabel(index)}>×</DeleteLabel>
                                                </Card>
                                            );
                                        })}
                                    <StyledForm onSubmit={this.addLabel}>
                                        <StyledInput
                                            style={{ width: 120 }}
                                            type="text"
                                            value={labelInput}
                                            onChange={this.changeLabelInput}
                                            onBlur={this.addLabel}
                                        />
                                    </StyledForm>
                                </div>
                            </FormElement>
                        </Column>
                        <Column>
                            {meta.opus.planType === "written" && (
                                <InputSelectCard
                                    name="composer"
                                    label={formatMessage(messages.composer)}
                                    value={meta.opus.composer || ""}
                                    onChange={this.changeOpusComposer}
                                    options={composers}
                                    save={this.saveOpusComposer}
                                    unknown
                                    createNew={{
                                        label: <FormattedMessage {...messages.addNewComposer} />,
                                        onClick: () => {
                                            this.setState({ createNewComposer: true });
                                        },
                                    }}
                                    required
                                />
                            )}
                            {meta.opus.planType !== "exercise" && (
                                <InputText
                                    label={formatMessage(messages.title)}
                                    value={meta.opus.title || ""}
                                    onChange={this.changeOpusTitle}
                                    onBlur={this.saveOpusTitle}
                                    required
                                />
                            )}
                            {meta.opus.planType === "exercise" && (
                                <Radio
                                    label={formatMessage(messages.exerciseType)}
                                    value={meta.opus.exerciseType || ""}
                                    onChange={this.changeOpusExerciseType}
                                    options={exerciseTypes(formatMessage)}
                                    required
                                />
                            )}
                            {meta.opus.planType === "written" && (
                                <InputSelectCard
                                    name="genre"
                                    label={formatMessage(messages.genre)}
                                    value={meta.opus.genre || ""}
                                    onChange={this.changeOpusGenre}
                                    options={genres}
                                    save={this.saveOpusGenre}
                                    required
                                />
                            )}
                            {meta.opus.planType === "folk" && (
                                <Radio
                                    label={formatMessage(messages.folksongTempo)}
                                    value={meta.opus.folksongTempo || ""}
                                    options={folksongTempos(formatMessage)}
                                    onChange={this.changeFolksongTempo}
                                    required
                                />
                            )}
                            {meta.opus.planType === "folk" && (
                                <Select
                                    name="folksongClass"
                                    label={formatMessage(messages.folksongClass)}
                                    value={meta.opus.folksongClass}
                                    onChange={this.changeOpusFolksongClass}
                                    options={folksongClasses(formatMessage)}
                                />
                            )}
                        </Column>
                    </>
                )}
                {createNewComposer && (
                    <ComposerForm
                        close={() => this.setState({ createNewComposer: false })}
                        submit={this.createComposer}
                    />
                )}
            </Tab>
        );
    }
}

export default Opus;
